/**
 * Partner Tracking Code DTOs
 * For CRM partner tracking and attribution
 */

import { z } from 'zod';
import {
  TrackingCodeStatus,
  TrackingCodeStatusSchema,
  TrackingSourceType,
  TrackingSourceTypeSchema,
} from '../enums/tracking.enums';

// Re-export for backwards compatibility
export { TrackingCodeStatus, TrackingCodeStatusSchema, TrackingSourceType, TrackingSourceTypeSchema };

// ========================================
// DTOs
// ========================================

/**
 * Tracking code DTO (response)
 */
export const TrackingCodeDtoSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string().uuid().nullable(),
  partnerName: z.string().nullable(),
  partnerEmail: z.string().email().nullable(),
  sourceType: TrackingSourceTypeSchema,
  code: z.string(),
  status: TrackingCodeStatusSchema,
  commissionRate: z.number().min(0).max(100).nullable(),
  totalClicks: z.number().int().nonnegative().default(0),
  totalLeads: z.number().int().nonnegative().default(0),
  convertedLeads: z.number().int().nonnegative().default(0),
  totalRevenue: z.number().nonnegative().default(0),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  lastClickAt: z.string().datetime().nullable(),
  lastConversionAt: z.string().datetime().nullable(),
});

export type TrackingCodeDto = z.infer<typeof TrackingCodeDtoSchema>;

/**
 * Create tracking code DTO
 */
export const CreateTrackingCodeDtoSchema = z.object({
  partnerId: z.string().uuid().optional(),
  sourceType: TrackingSourceTypeSchema.default(TrackingSourceType.PARTNER),
  code: z.string().min(3).max(50).optional(), // Auto-generated if not provided
  commissionRate: z.number().min(0).max(100).optional(),
  expiresAt: z.string().datetime().optional(),
});

export type CreateTrackingCodeDto = z.infer<typeof CreateTrackingCodeDtoSchema>;

/**
 * List tracking codes query params
 */
export const TrackingCodeQueryDtoSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  partnerId: z.string().uuid().optional(),
  sourceType: TrackingSourceTypeSchema.optional(),
  status: TrackingCodeStatusSchema.optional(),
  search: z.string().optional(),
});

export type TrackingCodeQueryDto = z.infer<typeof TrackingCodeQueryDtoSchema>;

/**
 * Paginated tracking codes response
 */
export const PaginatedTrackingCodesDtoSchema = z.object({
  data: z.array(TrackingCodeDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  }),
});

export type PaginatedTrackingCodesDto = z.infer<typeof PaginatedTrackingCodesDtoSchema>;
